import React, { useRef, useState, useCallback } from 'react';

/* ─── Canvas drawing ─────────────────────────────────────────── */
function drawAnn(ctx, ann, W, H) {
  ctx.save();
  const px = v => v * W, py = v => v * H;
  const sw = (ann.strokeWidth || 2) * (W / 794);
  switch (ann.type) {
    case 'pen': {
      const pts = ann.points || [];
      if (pts.length < 2) break;
      ctx.strokeStyle = ann.color || '#ef4444';
      ctx.lineWidth = sw; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
      ctx.beginPath(); ctx.moveTo(px(pts[0].x), py(pts[0].y));
      pts.slice(1).forEach(p => ctx.lineTo(px(p.x), py(p.y)));
      ctx.stroke(); break;
    }
    case 'highlight':
      ctx.globalAlpha = 0.35;
      ctx.fillStyle = ann.color || '#fbbf24';
      ctx.fillRect(px(ann.x), py(ann.y), px(ann.w), py(ann.h)); break;
    case 'rect':
      ctx.strokeStyle = ann.color || '#3b82f6';
      ctx.lineWidth = sw;
      ctx.strokeRect(px(ann.x), py(ann.y), px(ann.w), py(ann.h)); break;
    case 'arrow': {
      const [x1, y1, x2, y2] = [px(ann.x1), py(ann.y1), px(ann.x2), py(ann.y2)];
      const ang = Math.atan2(y2 - y1, x2 - x1), h = 12 * (W / 794);
      ctx.strokeStyle = ctx.fillStyle = ann.color || '#ef4444';
      ctx.lineWidth = sw;
      ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(x2, y2);
      ctx.lineTo(x2 - h * Math.cos(ang - Math.PI / 6), y2 - h * Math.sin(ang - Math.PI / 6));
      ctx.lineTo(x2 - h * Math.cos(ang + Math.PI / 6), y2 - h * Math.sin(ang + Math.PI / 6));
      ctx.closePath(); ctx.fill(); break;
    }
    case 'text':
      ctx.fillStyle = ann.color || '#0f172a';
      ctx.font = `${13 * (W / 794)}px "IBM Plex Sans", sans-serif`;
      (ann.text || '').split('\n').forEach((ln, i) =>
        ctx.fillText(ln, px(ann.x), py(ann.y) + i * 16 * (W / 794)));
      break;
    case 'sticky': {
      const [sx, sy] = [px(ann.x), py(ann.y)], r = 10 * (W / 794);
      ctx.fillStyle = '#fbbf24';
      ctx.beginPath(); ctx.arc(sx, sy, r, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#92400e';
      ctx.font = `bold ${11 * (W / 794)}px sans-serif`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText('!', sx, sy);
      ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic'; break;
    }
    default: break;
  }
  ctx.restore();
}

function hitTest(ann, cx, cy, W, H) {
  const px = v => v * W, py = v => v * H;
  const R = 14 * (W / 794);
  if (ann.type === 'rect' || ann.type === 'highlight') {
    return cx >= px(ann.x) && cx <= px(ann.x) + px(ann.w) &&
           cy >= py(ann.y) && cy <= py(ann.y) + py(ann.h);
  }
  if (ann.type === 'pen') {
    return (ann.points || []).some(p => Math.hypot(cx - px(p.x), cy - py(p.y)) < R);
  }
  const ax = px(ann.x ?? ann.x1 ?? 0), ay = py(ann.y ?? ann.y1 ?? 0);
  return Math.hypot(cx - ax, cy - ay) < R + 8;
}

/* ─── Sticky note hover tooltip ─────────────────────────────── */
function StickyTooltip({ ann }) {
  const [show, setShow] = useState(false);
  return (
    <div
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      style={{
        position: 'absolute', left: `${ann.x * 100}%`, top: `${ann.y * 100}%`,
        transform: 'translate(-50%, -50%)', width: 24, height: 24,
        zIndex: 20, pointerEvents: 'all',
      }}
    >
      {show && (
        <div style={{
          position: 'absolute', left: 28, top: -8,
          background: '#fef9c3', border: '1px solid #fbbf24', borderRadius: 4,
          padding: '7px 10px', maxWidth: 200, minWidth: 120,
          fontSize: 12, color: '#78350f', whiteSpace: 'pre-wrap',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)', zIndex: 30, pointerEvents: 'none',
        }}>
          {ann.text}
        </div>
      )}
    </div>
  );
}

/* ─── Inline text / sticky input ────────────────────────────── */
function InlineInput({ xPct, yPct, placeholder, rows, inputStyle, onSubmit, onCancel }) {
  const [val, setVal] = useState('');
  return (
    <div style={{ position: 'absolute', left: `${xPct}%`, top: `${yPct}%`, zIndex: 25, pointerEvents: 'all' }}>
      <textarea
        autoFocus
        rows={rows || 3}
        value={val}
        onChange={e => setVal(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSubmit(val); }
          if (e.key === 'Escape') onCancel();
        }}
        placeholder={placeholder}
        style={{
          padding: '4px 6px', fontSize: 12, width: 180, resize: 'none',
          outline: 'none', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          ...inputStyle,
        }}
      />
      <div style={{ display: 'flex', gap: 4, marginTop: 3 }}>
        <button
          onClick={() => onSubmit(val)}
          style={{ flex: 1, padding: '3px 8px', background: inputStyle?.accentColor || '#3b82f6', color: '#fff', border: 'none', borderRadius: 3, fontSize: 11, cursor: 'pointer' }}
        >
          Add
        </button>
        <button
          onClick={onCancel}
          style={{ padding: '3px 8px', background: '#e2e8f0', border: 'none', borderRadius: 3, fontSize: 11, cursor: 'pointer' }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

/* ─── Main AnnotationLayer component ────────────────────────── */
export default function AnnotationLayer({
  pageIdx, annotations, activeTool, activeColor, strokeWidth,
  onAdd, onRemove, pdfCanvasRef, active,
}) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [textPos, setTextPos] = useState(null);
  const [stickyPos, setStickyPos] = useState(null);

  // Sync overlay canvas size + redraw on every render
  // (no deps array — cheap canvas ops, always correct)
  React.useEffect(() => {
    const pdf = pdfCanvasRef?.current;
    const ov = canvasRef.current;
    if (!pdf || !ov) return;
    if (ov.width !== pdf.width || ov.height !== pdf.height) {
      ov.width = pdf.width || 0;
      ov.height = pdf.height || 0;
    }
    if (!ov.width || !ov.height) return;
    const ctx = ov.getContext('2d');
    ctx.clearRect(0, 0, ov.width, ov.height);
    annotations.forEach(a => drawAnn(ctx, a, ov.width, ov.height));
    if (draft) drawAnn(ctx, draft, ov.width, ov.height);
  });

  const getPos = useCallback(e => {
    const el = canvasRef.current;
    if (!el) return { x: 0, y: 0 };
    const r = el.getBoundingClientRect();
    return { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height };
  }, []);

  const mouseDown = useCallback(e => {
    if (!activeTool) return;
    e.preventDefault();
    const pos = getPos(e);

    if (activeTool === 'eraser') {
      const el = canvasRef.current;
      if (!el) return;
      const hit = [...annotations].reverse().find(a =>
        hitTest(a, pos.x * el.width, pos.y * el.height, el.width, el.height)
      );
      if (hit) onRemove(hit.id);
      return;
    }
    if (activeTool === 'text') { setTextPos(pos); return; }
    if (activeTool === 'sticky') { setStickyPos(pos); return; }

    setDrawing(true);
    setDraft(activeTool === 'pen'
      ? { type: 'pen', page: pageIdx, points: [pos], color: activeColor, strokeWidth }
      : { type: activeTool, page: pageIdx, x1: pos.x, y1: pos.y, x2: pos.x, y2: pos.y, color: activeColor, strokeWidth }
    );
  }, [activeTool, activeColor, strokeWidth, annotations, onRemove, pageIdx, getPos]);

  const mouseMove = useCallback(e => {
    if (!drawing || !draft) return;
    const pos = getPos(e);
    setDraft(prev => prev.type === 'pen'
      ? { ...prev, points: [...prev.points, pos] }
      : { ...prev, x2: pos.x, y2: pos.y }
    );
  }, [drawing, draft, getPos]);

  const mouseUp = useCallback(() => {
    if (!drawing || !draft) return;
    setDrawing(false);
    let fin = { ...draft, id: `ann_${Date.now()}` };
    if (fin.type === 'pen' && fin.points.length < 3) { setDraft(null); return; }
    if (['rect', 'highlight'].includes(fin.type)) {
      const x = Math.min(fin.x1, fin.x2), y = Math.min(fin.y1, fin.y2);
      const w = Math.abs(fin.x2 - fin.x1), h = Math.abs(fin.y2 - fin.y1);
      if (w < 0.005 || h < 0.005) { setDraft(null); return; }
      fin = { ...fin, x, y, w, h };
      ['x1', 'y1', 'x2', 'y2'].forEach(k => delete fin[k]);
    }
    onAdd(fin);
    setDraft(null);
  }, [drawing, draft, onAdd]);

  const newId = () => `ann_${Date.now()}`;

  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: 6 }}>
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%',
          cursor: active ? 'crosshair' : 'default',
          pointerEvents: active ? 'all' : 'none',
        }}
        onMouseDown={mouseDown}
        onMouseMove={mouseMove}
        onMouseUp={mouseUp}
        onMouseLeave={mouseUp}
      />

      {/* Sticky note hover tooltips */}
      {annotations.filter(a => a.type === 'sticky').map(a => (
        <StickyTooltip key={a.id} ann={a} />
      ))}

      {/* Text box input */}
      {textPos && (
        <InlineInput
          xPct={textPos.x * 100} yPct={textPos.y * 100}
          placeholder="Type text… (Enter to confirm)"
          inputStyle={{ border: `2px solid ${activeColor}`, background: 'white', color: '#0f172a', accentColor: activeColor }}
          onSubmit={text => {
            if (text.trim()) onAdd({ id: newId(), type: 'text', page: pageIdx, x: textPos.x, y: textPos.y, text, color: activeColor });
            setTextPos(null);
          }}
          onCancel={() => setTextPos(null)}
        />
      )}

      {/* Sticky note input */}
      {stickyPos && (
        <InlineInput
          xPct={stickyPos.x * 100} yPct={stickyPos.y * 100} rows={4}
          placeholder="Add sticky note… (Enter to confirm)"
          inputStyle={{ border: '2px solid #fbbf24', background: '#fef9c3', color: '#78350f', accentColor: '#f59e0b' }}
          onSubmit={text => {
            if (text.trim()) onAdd({ id: newId(), type: 'sticky', page: pageIdx, x: stickyPos.x, y: stickyPos.y, text });
            setStickyPos(null);
          }}
          onCancel={() => setStickyPos(null)}
        />
      )}
    </div>
  );
}
